import React, { useRef, useEffect, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Layout from '../../components/layout/layout'
import './aboutUs.scss'
import Reveal from '../../components/Reveal/Reveal'
import SplitHeading from '../../components/motion/SplitHeading'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import Seo, { breadcrumbJsonLd } from '../../components/Seo/Seo'
import useEntryMotion from '../../components/motion/useEntryMotion'
import PageBanner from '../../components/PageBanner/PageBanner'
import HeroVideo from '../../components/HeroVideo/HeroVideo'
import projects, { resolveProjects } from '../../data/projects'

import { resolveTeam } from '../../data/team'

/**
 * About page: who we are (hero with facts that are all countable from the
 * site's own data), the team, what we have built, our values, and a way to
 * start a conversation.
 */
const AboutUs = () => {
  const animateIn = useEntryMotion()
  const navigate = useNavigate()
  const { translations: t } = useContext(LanguageContext)
  const teamRef = useRef(null)
  const valuesRef = useRef(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const teamMembers = resolveTeam(t).map((m) => ({ src: m.src, memberName: m.name, profession: m.role }))

  const facts = [
    { value: String(teamMembers.length), label: t.aboutFact1 },
    { value: String(projects.length), label: t.aboutFact2 },
    { value: 'GMT+4', label: t.aboutFact3 },
    { value: '3', label: t.aboutFact4 },
  ]

  const built = resolveProjects(t)

  const valuesBox = [
    { number: '01', title: t.aboutVaueTitleOne, txt: t.aboutVaueTextOne },
    { number: '02', title: t.aboutVaueTitleTwo, txt: t.aboutVaueTextTwo },
    { number: '03', title: t.aboutVaueTitleThree, txt: t.aboutVaueTextThree },
    { number: '04', title: t.aboutVaueTitleFour, txt: t.aboutVaueTextFour },
  ]

  const scrollTo = (ref) => ref.current && ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })

  const rise = (delay) => ({
    initial: animateIn ? { opacity: 0, y: 30 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  })

  return (
    <Layout>
      <Seo
        title='About Us — The Team Behind MLMind'
        description='Meet the MLMind team in Tbilisi: machine learning engineers, developers and designers building AI products, websites and apps for companies in Georgia and Europe.'
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'About us', path: '/about-us' },
        ])}
      />

      {/* Hero */}
      <section className='about-hero'>
        <HeroVideo />
        <div className='about-hero-inner'>
          <div className='about-hero-copy'>
            <motion.p className='about-kicker' {...rise(0)}>{t.aboutKicker}</motion.p>
            <motion.h1 className='about-hero-title' {...rise(0.08)}>{t.aboutTitle}</motion.h1>
            <motion.p className='about-hero-lead' {...rise(0.16)}>{t.aboutLead}</motion.p>
            <motion.div className='about-hero-actions' {...rise(0.24)}>
              <button type='button' className='about-btn about-btn-accent' onClick={() => scrollTo(teamRef)}>{t.OurTeam}</button>
              <button type='button' className='about-btn about-btn-ghost' onClick={() => navigate('/contact-us')}>{t.homeStartProject} <span aria-hidden='true'>→</span></button>
            </motion.div>
          </div>
          <motion.dl className='about-facts' {...rise(0.3)}>
            {facts.map((fact) => (
              <div key={fact.label} className='about-fact'>
                <dt>{fact.value}</dt>
                <dd>{fact.label}</dd>
              </div>
            ))}
          </motion.dl>
        </div>
      </section>

      <div className='about-page-container'>
        <div className='about-page-content'>
          {/* Team */}
          <section ref={teamRef} className='about-section'>
            <Reveal>
              <p className='about-eyebrow'>{t.aboutDiscoverWhoWeAre}</p>
              <SplitHeading text={t.OurTeam} className='about-h2' />
              <p className='about-intro'>{t.aboutOurAimes}</p>
            </Reveal>
            <div className='about-team-grid'>
              {teamMembers.map((member, index) => (
                <Reveal key={member.memberName} delay={(index % 4) * 0.08} className='about-member'>
                  <div className='about-member-photo'>
                    <img src={member.src} alt={member.memberName} loading='lazy' />
                  </div>
                  <div className='about-member-text'>
                    <p className='about-member-name'>{member.memberName}</p>
                    <p className='about-member-role'>{member.profession}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* What we have built */}
          <section className='about-section'>
            <Reveal>
              <p className='about-eyebrow'>{t.aboutBuiltKicker}</p>
              <SplitHeading text={t.aboutBuiltTitle} className='about-h2' />
            </Reveal>
            <ul className='about-built'>
              {built.map((project, index) => (
                <Reveal key={project.title} delay={index * 0.06}>
                  <li>
                    <Link to={project.link} className='about-built-row'>
                      <span className='about-built-num'>0{index + 1}</span>
                      <span className='about-built-name'>{project.title}</span>
                      <span className='about-built-tag'>{project.tag}</span>
                      <span className='about-built-arrow' aria-hidden='true'>→</span>
                    </Link>
                  </li>
                </Reveal>
              ))}
            </ul>
          </section>

          {/* Values */}
          <section ref={valuesRef} className='about-section about-values'>
            <Reveal>
              <p className='about-eyebrow'>{t.aboutValuesKicker}</p>
              <SplitHeading text={t.OurValues} className='about-h2' />
            </Reveal>
            <div className='about-values-grid'>
              {valuesBox.map((item, index) => (
                <Reveal key={item.number} delay={index * 0.08} className='about-value'>
                  <p className='about-value-num'>{t.Value} {item.number}</p>
                  <h3 className='about-value-title'>{item.title}</h3>
                  <p className='about-value-text'>{item.txt}</p>
                </Reveal>
              ))}
            </div>
          </section>
        </div>
      </div>

      <PageBanner
        eyebrow={t.bannerAboutEyebrow}
        title={t.bannerAboutTitle}
        highlight={t.bannerAboutHighlight}
        text={t.bannerAboutText}
        cta={t.bannerAboutCta}
        to='/contact-us'
        shape='loop'
        colors={['#4c1d95', '#e7ff02', '#f472b6']}
      />
    </Layout>
  )
}

export default AboutUs
